import React, { useEffect, useState } from "react";
import { useAuth } from "../AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { StatusBadge } from "./Dashboard";
import { Calendar, Clock, ExternalLink, Video } from "lucide-react";
import ChunkedUploader from "../components/ChunkedUploader";
import SecureVideoPlayer from "../components/SecureVideoPlayer";

export function NewWebinar() {
  const { user, API } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    title: "", description: "", category: "Design",
    scheduled_at: "", meeting_link: "", price: 299, duration_minutes: 60,
    cover_image: "https://images.unsplash.com/photo-1591115765373-5207764f72e7?w=800",
  });

  useEffect(() => {
    axios.get(`${API}/categories`, { withCredentials: true }).then((r) => setCategories(r.data));
  }, []);

  if (user?.role !== "teacher") return <div>Only teachers can plan webinars.</div>;
  if (user?.teacher_status !== "approved") {
    return <div className="card-surface p-8" data-testid="teacher-not-approved-webinar">
      Your teacher account must be approved by IT Admin before you can plan webinars.
    </div>;
  }

  const submit = async (e) => {
    e.preventDefault();
    try {
      const r = await axios.post(`${API}/webinars`, form, { withCredentials: true });
      toast.success("Webinar created (draft)");
      navigate(`/teacher/webinars/${r.data.id}/edit`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create");
    }
  };

  return (
    <div className="max-w-3xl">
      <div className="overline">Studio</div>
      <h1 className="font-heading text-3xl font-semibold text-[#0A231C] mb-8">Plan a new webinar</h1>
      <form onSubmit={submit} className="card-surface p-8 space-y-5" data-testid="new-webinar-form">
        <Field label="Title"><input required className="input" value={form.title}
          onChange={(e) => setForm({...form, title: e.target.value})} data-testid="webinar-title-input" /></Field>
        <Field label="Description"><textarea required rows={4} className="input" value={form.description}
          onChange={(e) => setForm({...form, description: e.target.value})} data-testid="webinar-desc-input" /></Field>
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Category">
            <select className="input" value={form.category}
              onChange={(e) => setForm({...form, category: e.target.value})} data-testid="webinar-category-select">
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Duration (minutes)">
            <input type="number" min={15} className="input" value={form.duration_minutes}
              onChange={(e) => setForm({...form, duration_minutes: parseInt(e.target.value || 0)})} />
          </Field>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Scheduled at">
            <input type="datetime-local" required className="input" value={form.scheduled_at}
              onChange={(e) => setForm({...form, scheduled_at: e.target.value})} data-testid="webinar-schedule-input" />
          </Field>
          <Field label="Suggested price (INR)">
            <input type="number" min={0} className="input" value={form.price}
              onChange={(e) => setForm({...form, price: parseFloat(e.target.value || 0)})} data-testid="webinar-price-input" />
          </Field>
        </div>
        <Field label="Meeting link (Zoom / Meet / etc.)">
          <input required className="input" value={form.meeting_link}
            onChange={(e) => setForm({...form, meeting_link: e.target.value})} data-testid="webinar-link-input" />
        </Field>
        <Field label="Cover image URL">
          <input className="input" value={form.cover_image}
            onChange={(e) => setForm({...form, cover_image: e.target.value})} />
        </Field>
        <div className="pt-4">
          <button type="submit" className="btn-primary" data-testid="create-webinar-submit">Create draft</button>
        </div>
      </form>
      <style>{`.input{width:100%;padding:0.65rem 0.9rem;border:1px solid #E5E3D8;border-radius:8px;background:white;outline:none;font-family:inherit}.input:focus{border-color:#0A231C}`}</style>
    </div>
  );
}

export function EditWebinar() {
  const { id } = useParams();
  const { user, API } = useAuth();
  const navigate = useNavigate();
  const [w, setW] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => axios.get(`${API}/webinars/${id}`, { withCredentials: true }).then((r) => setW(r.data.webinar));
  useEffect(() => { load(); }, [id]);

  if (!w) return <div className="p-10">Loading…</div>;

  const patch = (k, v) => setW({ ...w, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/webinars/${id}`, {
        title: w.title, description: w.description, category: w.category,
        scheduled_at: w.scheduled_at, meeting_link: w.meeting_link,
        duration_minutes: w.duration_minutes, cover_image: w.cover_image,
      }, { withCredentials: true });
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const submitForReview = async () => {
    await axios.post(`${API}/webinars/${id}/submit`, {}, { withCredentials: true });
    toast.success("Submitted for approval");
    load();
  };

  const scheduledLocal = w.scheduled_at?.length > 16 ? w.scheduled_at.slice(0, 16) : w.scheduled_at;

  return (
    <div className="max-w-3xl space-y-6" data-testid="edit-webinar-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline">Editing</div>
          <h1 className="font-heading text-3xl font-semibold text-[#0A231C]">{w.title}</h1>
        </div>
        <StatusBadge s={w.status || "draft"} />
      </div>

      <div className="card-surface p-6 space-y-4">
        <Field label="Title"><input className="input" value={w.title} onChange={(e) => patch("title", e.target.value)} /></Field>
        <Field label="Description"><textarea rows={3} className="input" value={w.description} onChange={(e) => patch("description", e.target.value)} /></Field>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Category"><input className="input" value={w.category} onChange={(e) => patch("category", e.target.value)} /></Field>
          <Field label="Duration (min)"><input type="number" className="input" value={w.duration_minutes} onChange={(e) => patch("duration_minutes", parseInt(e.target.value||0))} /></Field>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Scheduled at"><input type="datetime-local" className="input" value={scheduledLocal} onChange={(e) => patch("scheduled_at", e.target.value)} /></Field>
          <Field label="Price (INR, set by admin)"><input className="input" value={w.price} disabled /></Field>
        </div>
        <Field label="Meeting link"><input className="input" value={w.meeting_link} onChange={(e) => patch("meeting_link", e.target.value)} /></Field>
        <Field label="Cover image URL"><input className="input" value={w.cover_image || ""} onChange={(e) => patch("cover_image", e.target.value)} /></Field>
        <div className="flex gap-3 pt-2">
          <button onClick={save} disabled={saving} className="btn-outline" data-testid="save-webinar-btn">{saving ? "Saving…" : "Save changes"}</button>
          {w.status === "draft" && (
            <button onClick={submitForReview} className="btn-primary" data-testid="submit-webinar-btn">Submit for approval</button>
          )}
        </div>
      </div>

      {w.status === "approved" && (
        <div className="card-surface p-6 space-y-3">
          <div className="overline">Recording library</div>
          {w.has_recording ? (
            <div className="text-sm text-[#2E7D32]">✓ Recording uploaded. Registered learners can watch it.</div>
          ) : (
            <p className="text-sm text-[#5C5C5C]">
              Upload the session recording after the webinar so learners who missed the live can watch it later.
            </p>
          )}
          <ChunkedUploader webinarId={w.id} onUploaded={load} />
        </div>
      )}
      <style>{`.input{width:100%;padding:0.6rem 0.85rem;border:1px solid #E5E3D8;border-radius:8px;background:white;outline:none;font-family:inherit;font-size:0.9rem}.input:focus{border-color:#0A231C}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="overline mb-1">{label}</div>
      {children}
    </label>
  );
}

export function WebinarDetail() {
  const { id } = useParams();
  const { user, API } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = () => axios.get(`${API}/webinars/${id}`, { withCredentials: true }).then((r) => setData(r.data));
  useEffect(() => { load(); }, [id]);

  if (!data) return <div className="p-10">Loading…</div>;
  const w = data.webinar;
  const enrolled = data.enrolled;

  const enroll = async () => {
    try {
      const r = await axios.post(`${API}/webinars/${id}/enroll`, {}, { withCredentials: true });
      toast.success(r.data.already ? "Already registered" : "Registered! Join link is now available.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to register");
    }
  };

  return (
    <div className="grid md:grid-cols-12 gap-8" data-testid="webinar-detail">
      <section className="md:col-span-8 space-y-4">
        <div className="flex items-center gap-3">
          <div className="overline">{w.category}</div>
          <StatusBadge s={w.status || "draft"} />
        </div>
        <h1 className="font-heading text-3xl md:text-4xl font-semibold text-[#0A231C]">{w.title}</h1>
        <div className="flex flex-wrap items-center gap-5 text-sm text-[#5C5C5C]">
          <span>by <b className="text-[#1C1C1C]">{w.instructor_name}</b></span>
          <span className="flex items-center gap-1"><Calendar size={14} /> {new Date(w.scheduled_at).toLocaleString()}</span>
          <span className="flex items-center gap-1"><Clock size={14} /> {w.duration_minutes || 60} min</span>
          <span>{w.price > 0 ? `₹${w.price}` : "Free"}</span>
        </div>
        {w.cover_image && <img src={w.cover_image} alt="" className="w-full h-72 object-cover rounded-2xl" />}
        <p className="text-[#5C5C5C] leading-relaxed whitespace-pre-line">{w.description}</p>

        {enrolled && w.has_recording && w.recording_video_id && (
          <div className="card-surface p-4" data-testid="webinar-recording">
            <div className="overline mb-2 flex items-center gap-2"><Video size={12} /> Session recording</div>
            <SecureVideoPlayer videoId={w.recording_video_id} courseId={w.id} />
          </div>
        )}
      </section>

      <aside className="md:col-span-4">
        <div className="card-surface p-6 sticky top-24" data-testid="webinar-enroll-panel">
          <div className="overline">Registration</div>
          <div className="mt-1 font-heading text-3xl font-semibold text-[#0A231C]">
            {w.price > 0 ? `₹${w.price}` : "Free"}
          </div>
          {user?.role === "learner" && (
            enrolled ? (
              <>
                <div className="mt-3 text-sm text-[#2E7D32] font-medium">✓ You're registered</div>
                <a href={w.meeting_link} target="_blank" rel="noreferrer"
                   className="btn-primary w-full mt-4 flex items-center justify-center gap-2"
                   data-testid="join-webinar-link">
                  Join meeting <ExternalLink size={14} />
                </a>
              </>
            ) : (
              w.status === "approved" && (
                <button onClick={enroll} className="btn-primary w-full mt-4" data-testid="register-webinar-btn">
                  {w.price > 0 ? `Pay ₹${w.price} and register` : "Register free"}
                </button>
              )
            )
          )}
          {user?.role === "teacher" && w.instructor_id === user.user_id && (
            <button onClick={() => navigate(`/teacher/webinars/${id}/edit`)} className="btn-outline w-full mt-4 text-sm" data-testid="edit-webinar-btn">
              Edit webinar
            </button>
          )}
          {user?.role === "admin" && (
            <a href={w.meeting_link} target="_blank" rel="noreferrer" className="btn-outline w-full mt-4 text-sm flex items-center justify-center gap-2">
              Open link <ExternalLink size={14} />
            </a>
          )}
        </div>
      </aside>
    </div>
  );
}
